// Argus — ResizableHeader: header cell <th> cu drag handle pe right edge pentru resize +
// HTML5 native draggable pentru reorder.

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface ResizableHeaderProps {
  width: number;
  onResize: (newWidth: number) => void;
  draggable?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragOver?: () => void;
  onDrop?: () => void;
  isDragOver?: boolean;
  onHeaderClick: (() => void) | undefined;
  children: ReactNode;
}

const HEADER_BORDER = '1px solid rgba(255,255,255,0.08)';

export function ResizableHeader({
  width,
  onResize,
  draggable = true,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragOver = false,
  onHeaderClick,
  children,
}: ResizableHeaderProps): JSX.Element {
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWRef = useRef(0);

  useEffect(() => {
    if (!isResizing) return;
    const handleMove = (e: MouseEvent) => {
      const dx = e.clientX - startXRef.current;
      onResize(startWRef.current + dx);
    };
    const handleUp = () => setIsResizing(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isResizing, onResize]);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWRef.current = width;
    setIsResizing(true);
  };

  // Bloc v1.0.10 fix UX — sort click izolat într-un inner div (NU mai depinde de bubbling
  // peste draggable th + dataset.resizeHandle filter, care în practică suprima click pe
  // coloane non-Name din motive subtile de drag interaction. Drag/drop reorder rămâne pe th
  // (entire cell), resize handle separate (absolute right). Click pe inner div = pure sort.
  return (
    <th
      style={{
        width,
        minWidth: width,
        maxWidth: width,
        padding: 0,
        textAlign: 'left',
        fontWeight: 600,
        fontSize: 13,
        borderBottom: HEADER_BORDER,
        position: 'relative',
        userSelect: 'none',
        backgroundColor: isDragOver ? 'rgba(59, 130, 246, 0.15)' : undefined,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.();
      }}
      onDragEnd={() => onDragEnd?.()}
      onDragOver={(e) => {
        if (!draggable) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOver?.();
      }}
      onDrop={(e) => {
        if (!draggable) return;
        e.preventDefault();
        onDrop?.();
      }}
    >
      <div
        onClick={() => onHeaderClick?.()}
        style={{
          padding: '4px 8px',
          paddingRight: 16,
          cursor: onHeaderClick ? 'pointer' : (draggable ? 'move' : 'default'),
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>
      <span
        onMouseDown={startResize}
        onClick={(e) => e.stopPropagation()}
        title="Resize column"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 8,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'transparent',
        }}
        onMouseEnter={(e) => {
          const bar = e.currentTarget.firstElementChild as HTMLElement | null;
          if (bar && !isResizing) bar.style.background = 'rgba(59,130,246,0.7)';
        }}
        onMouseLeave={(e) => {
          const bar = e.currentTarget.firstElementChild as HTMLElement | null;
          if (bar && !isResizing) bar.style.background = 'rgba(255,255,255,0.22)';
        }}
      >
        <span
          style={{
            width: 2,
            height: '60%',
            background: isResizing ? 'rgba(59,130,246,0.85)' : 'rgba(255,255,255,0.22)',
            borderRadius: 1,
            transition: 'background 120ms',
          }}
        />
      </span>
    </th>
  );
}
